import { Component, inject, OnInit } from '@angular/core';
import { ConfigurationsService } from '../../services/configurations.service';
import { catchError, first, forkJoin, of, switchMap } from 'rxjs';
import { GungNotificationService, OptionsListMulti } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from '../../services/users/users.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { GungFlow } from '../../state/flow/types';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HomeNavigationConfigService } from '../../services/home-navigation-config/home-navigation-config.service';

@Component({
  selector: 'lib-home-navigation-config',
  templateUrl: './home-navigation-config.component.html',
  styleUrl: './home-navigation-config.component.scss',
  standalone: false
})
export class HomeNavigationConfigComponent implements OnInit {
  protected configurationService = inject(ConfigurationsService);
  protected gungNotificationService = inject(GungNotificationService);
  protected translateService = inject(TranslateService);
  protected usersService = inject(UsersService);
  protected gungFlowService = inject(GungFlowService);
  protected homeNavigationConfigService = inject(HomeNavigationConfigService);

  private readonly configurationId = 'HomePageConfiguration';

  homePageConfiguration: HomePageConfiguration;
  rolesToShow: string[] = [];
  hasRoleError: { [key: string]: boolean } = {};

  protected homePageMatchGroupsEnabled: boolean = false;
  protected get homepageDateRangeEnabled(): boolean {
    return this.homeNavigationConfigService.homepageDateRangeEnabled;
  }
  protected homepageCountryEnabled: boolean = this.homeNavigationConfigService.homepageCountryEnabled;
  protected placeholderColSpan: number =
    6 + (this.homeNavigationConfigService.homepageDateRangeEnabled ? 1 : 0) + (this.homepageCountryEnabled ? 1 : 0);
  protected availableUserRoleOptions: OptionsListMulti[] = [];
  protected availableUserGroupOptions: OptionsListMulti[] = [];
  protected availableGungFlowOptions: OptionsListMulti[] = [];
  protected availableCountryOptions: OptionsListMulti[] = [];
  protected homePageMatchGroupsTableData: HomePageMatchGroupsTableData[] = [];
  protected oldHomePagePathWarnings: { role: string, path: string }[] = [];
  protected hasDateRangeError: boolean[] = [];

  ngOnInit(): void {
    this.usersService.getDefaultUserRoles().pipe(
      first(),
      switchMap(roles =>
        forkJoin([of(roles), this.configurationService.getConfigurationsByIds([this.configurationId]).pipe(first())])
      )
    ).subscribe(([roles, configuration]) => {
      if (!configuration || configuration.length === 0) {
        return;
      }

      const config = configuration[0];
      this.rolesToShow = roles.map((role: { id: string }) => role.id).filter((role: string) => role !== 'ACTUATOR');
      this.rolesToShow.forEach(role => {
        if (!config.roleHomePages[role]) {
          config.roleHomePages[role] = '';
        }
      });
      this.homePageConfiguration = config;

      this.checkForHomePageMatchGroups();
      if (this.homePageMatchGroupsEnabled) {
        this.mapHomePageMatchGroupsDisplay();
      }
    });
  }

  protected save(): void {
    if (this.homePageMatchGroupsEnabled) {
      if (this.homepageDateRangeEnabled && !this.validateDateRanges()) {
        return;
      }

      this.homePageConfiguration.homePageMatchGroups = this.mapTableDataToConfiguration();

      if (this.oldHomePagePathWarnings.length > 0) {
        for (const warning of this.oldHomePagePathWarnings) {
          this.homePageConfiguration.roleHomePages[warning.role] = '';
        }
      }
    }

    for (const role of this.rolesToShow) {
      if (this.hasRoleError[role]) {
        return;
      }
    }

    this.configurationService.updateConfiguration(this.homePageConfiguration).pipe(
      first(),
      catchError(() => {
        this.gungNotificationService.notify(
          this.translateService.instant('ERROR_SAVING_CONFIGURATION'),
          this.translateService.instant('ERROR_SAVING_CONFIGURATION'),
          'error'
        );
        return of(null);
      })
    ).subscribe(res => {
      if (!res) {
        return;
      }
      this.gungNotificationService.notify(
        this.translateService.instant('CONFIGURATION_SAVED'),
        this.translateService.instant('CONFIGURATION_SAVED'),
        'success'
      );

      if (this.oldHomePagePathWarnings.length > 0) {
        this.oldHomePagePathWarnings = [];
      }
    });
  }

  protected validateInput(value: string, role: string) {
    if (!value) {
      return;
    }


    if (!value.startsWith('/')) {
      this.hasRoleError[role] = true;
    } else {
      this.hasRoleError[role] = false;
    }
  }

  protected checkForHomePageMatchGroups(): void {
    // Check if home page match group exists in the configuration //
    this.homePageMatchGroupsEnabled = !!this.homePageConfiguration?.hasOwnProperty('homePageMatchGroups');
  }

  protected mapHomePageMatchGroupsDisplay(): void {
    forkJoin({
      roles: this.usersService.getDefaultUserRoles().pipe(
        first(),
        switchMap(roles => of(roles.map((role: { selected: false, id: string }) => role?.id || role)))
      ),
      groups: this.gungFlowService.getSystemGung().pipe(
        first(),
        catchError(() => of(undefined)),
        switchMap(settings => of(settings?.extra?.userGroups || []))
      ),
      flows: this.gungFlowService.getGungAdminFlows().pipe(first()),
      countryCodes: this.homepageCountryEnabled
        ? this.homeNavigationConfigService.getAvailableCountryCodes().pipe(first())
        : of([] as string[])
    }).subscribe(({ roles, groups, flows, countryCodes }) => {
      // Get available options //
      this.availableUserRoleOptions = roles?.map((role: string[]) => { return { id: role, name: (!!role ? this.translateService.instant(role) : ''), selected: false } }) || [];
      this.availableUserGroupOptions = groups?.map((group: string[]) => { return { id: group, name: (!!group ? this.translateService.instant(group) : ''), selected: false } }) || [];
      this.availableGungFlowOptions = flows?.map(flow => { return { id: flow.id, name: (!!flow.name ? this.translateService.instant(flow?.name) : ''), selected: false } }) || [];
      this.availableCountryOptions = countryCodes?.map(code => { return { id: code, name: code, selected: false } }) || [];

      // Map existing page matches to table data //
      if (this.homePageConfiguration?.homePageMatchGroups && this.homePageConfiguration.homePageMatchGroups.length > 0) {
        for (const pageMatch of this.homePageConfiguration.homePageMatchGroups) {
          this.homePageMatchGroupsTableData.push({
            userRoleOptions: this.availableUserRoleOptions.map(o => ({ ...o, selected: pageMatch.roles.includes(o.id) })),
            userGroupOptions: this.availableUserGroupOptions.map(o => ({ ...o, selected: pageMatch.groups.includes(o.id) })),
            flowOptions: this.availableGungFlowOptions.map(o => ({ ...o, selected: pageMatch.flowIds.includes(o.id) })),
            countryOptions: this.mapCountryOptions(pageMatch.countryCodes ?? []),
            countryCodes: pageMatch.countryCodes ?? [],
            pagePath: pageMatch.pagePath,
            startDate: pageMatch.startDate != null ? new Date(pageMatch.startDate) : null,
            endDate: pageMatch.endDate != null ? new Date(pageMatch.endDate) : null
          });
        }
      }

      // Show old home page path warnings if there are no home page match groups and there are old page paths //
      if (
        this.homePageConfiguration?.homePageMatchGroups &&
        this.homePageConfiguration.homePageMatchGroups.length === 0 &&
        Object.values(this.homePageConfiguration?.roleHomePages || {}).filter(path => !!path).length > 0
      ) {
        for (const role in this.homePageConfiguration.roleHomePages) {
          if (this.homePageConfiguration.roleHomePages.hasOwnProperty(role)) {
            const path = this.homePageConfiguration.roleHomePages[role];
            if (!!path) {
              this.oldHomePagePathWarnings.push({ role, path });
            }
          }
        }
      }

      // Add initial configuration row if there is none //
      if (this.homePageMatchGroupsTableData.length === 0) {
        this.addRow();
      }
    });
  }

  protected mapTableDataToConfiguration(): HomePageMatchGroups[] {
    const useDates = this.homepageDateRangeEnabled;
    return this.homePageMatchGroupsTableData.map(data => {
      return {
        roles: data.userRoleOptions.filter(o => !!o.selected).map(o => o.id),
        groups: data.userGroupOptions.filter(o => !!o.selected).map(o => o.id),
        flowIds: data.flowOptions.filter(o => !!o.selected).map(o => o.id),
        // only changed through the Country column, so saving with the flag off keeps the stored codes
        countryCodes: data.countryCodes,
        pagePath: data.pagePath,
        startDate: useDates && data.startDate ? this.toStartOfDayMs(data.startDate) : null,
        endDate: useDates && data.endDate ? this.toEndOfDayMs(data.endDate) : null
      };
    }).filter(pageMatch => !!pageMatch.pagePath);
  }

  protected addRow(): void {
    // fresh copies with nothing selected, never the shared available option arrays themselves
    this.homePageMatchGroupsTableData.push({
      userRoleOptions: this.availableUserRoleOptions.map(o => ({ ...o, selected: false })),
      userGroupOptions: this.availableUserGroupOptions.map(o => ({ ...o, selected: false })),
      flowOptions: this.availableGungFlowOptions.map(o => ({ ...o, selected: false })),
      countryOptions: this.mapCountryOptions([]),
      countryCodes: [],
      pagePath: '',
      startDate: null,
      endDate: null
    });
  }

  protected setStartDate(date: Date, index: number): void {
    const row = this.homePageMatchGroupsTableData[index];
    row.startDate = date ?? null;
    this.validateDateRange(index);
  }

  protected setEndDate(date: Date, index: number): void {
    const row = this.homePageMatchGroupsTableData[index];
    row.endDate = date ?? null;
    this.validateDateRange(index);
  }

  protected validateDateRange(index: number): void {
    const row = this.homePageMatchGroupsTableData[index];
    if (row.startDate == null || row.endDate == null) {
      this.hasDateRangeError[index] = false;
      return;
    }
    this.hasDateRangeError[index] = row.startDate.getTime() > row.endDate.getTime();
  }

  protected validateDateRanges(): boolean {
    for (let i = 0; i < this.homePageMatchGroupsTableData.length; i++) {
      this.validateDateRange(i);
    }
    return !this.hasDateRangeError.some(e => !!e);
  }

  protected isRowCurrentlyActive(row: HomePageMatchGroupsTableData): boolean {
    if (!this.homepageDateRangeEnabled) {
      return true;
    }
    const now = Date.now();
    const startOk = row.startDate == null || now >= this.toStartOfDayMs(row.startDate);
    const endOk = row.endDate == null || now <= this.toEndOfDayMs(row.endDate);
    return startOk && endOk;
  }

  private toStartOfDayMs(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

  private toEndOfDayMs(date: Date): number {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }

  protected onDrop(event: CdkDragDrop<HomePageMatchGroupsTableData[]>) {
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  protected removeRow(index: number) {
    this.homePageMatchGroupsTableData.splice(index, 1);
  }

  protected setRoleValue(values: string[], index: number): void {
    const options: OptionsListMulti[] = structuredClone(this.homePageMatchGroupsTableData[index].userRoleOptions);
    for (const option of options) {
      option.selected = values.includes(option.id);
    }
    this.homePageMatchGroupsTableData[index].userRoleOptions = options;
  }

  protected setGroupValue(values: string[], index: number): void {
    const options: OptionsListMulti[] = structuredClone(this.homePageMatchGroupsTableData[index].userGroupOptions);
    for (const option of options) {
      option.selected = values.includes(option.id);
    }
    this.homePageMatchGroupsTableData[index].userGroupOptions = options;
  }

  protected setFlowValue(values: string[], index: number): void {
    const options: OptionsListMulti[] = structuredClone(this.homePageMatchGroupsTableData[index].flowOptions);
    for (const option of options) {
      option.selected = values.includes(option.id);
    }
    this.homePageMatchGroupsTableData[index].flowOptions = options;
  }

  protected setCountryValue(values: string[], index: number): void {
    const row = this.homePageMatchGroupsTableData[index];
    row.countryOptions = this.mapCountryOptions(values);
    row.countryCodes = [...values];
  }

  protected mapCountryOptions(countryCodes: string[]): OptionsListMulti[] {
    return this.availableCountryOptions.map(o => ({ ...o, selected: countryCodes.includes(o.id) }));
  }
}

export interface HomePageConfiguration {
  roleHomePages: { [role: string]: string };
  homePageMatchGroups?: HomePageMatchGroups[]
}

export interface HomePageMatchGroups {
  roles: string[],
  groups: string[],
  flowIds: string[],
  countryCodes?: string[],
  pagePath: string,
  startDate?: number | null,
  endDate?: number | null
}

interface HomePageMatchGroupsTableData {
  userRoleOptions: OptionsListMulti[],
  userGroupOptions: OptionsListMulti[],
  flowOptions: OptionsListMulti[],
  countryOptions: OptionsListMulti[],
  countryCodes: string[],
  pagePath: string,
  startDate: Date | null,
  endDate: Date | null
}
